import type { ApsOcrExtraction } from '@common/types/apsOcr';
import { normalizeApsOcrExtraction } from '@common/utils/apsOcrPrefill';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { Box, Stack, Typography, useTheme } from '@mui/material';
import { memo, useEffect, useState } from 'react';
import { scanApsPdf } from '@/services/OcrService';
import { errorSnackbar } from '@/utils/snackbar';
import NewProspectStep from '../NewProspectStep';
import {
    ACCEPTED_FILE_TYPE,
    ARROW_SLOT_SIZE,
    LAYOUT_EXPAND_MS,
    MAX_FIELD_ROW_WIDTH,
    MAX_FILE_SIZE,
} from './constants';
import { getDisplayRows, getExtraExtractedFieldGroups } from './extractionDisplay';
import ExtraExtractedProperties from './ExtraExtractedProperties';
import OcrResultRows from './OcrResultRows';
import PdfFirstPagePreview from './PdfFirstPagePreview';
import PdfUploadDropzone from './PdfUploadDropzone';
import { resolveScannedAddress } from './resolveScannedAddress';
import ScanOverlay from './ScanOverlay';

interface Props {
    scannedExtraction: ApsOcrExtraction | null;
    onScannedExtractionChange(extraction: ApsOcrExtraction | null): void;
    /** Matter field values, including edits made in other steps. */
    fieldValues: Record<string, string>;
    onFieldChange(key: string, value: string): void;
    onApsScanIdChange(scanId: string | null): void;
    highlighted?: boolean;
}

export default memo(function ApsOcrScan({
    scannedExtraction,
    onScannedExtractionChange,
    fieldValues,
    onFieldChange,
    onApsScanIdChange,
    highlighted = false,
}: Props) {
    const theme = useTheme();
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [isScanning, setIsScanning] = useState(false);
    const [fieldsSlotWidth, setFieldsSlotWidth] = useState(0);
    const [arrowSlotSize, setArrowSlotSize] = useState(0);
    const [showArrow, setShowArrow] = useState(false);
    const [showResults, setShowResults] = useState(false);

    useEffect(() => {
        if (!scannedExtraction) {
            setFieldsSlotWidth(0);
            setArrowSlotSize(0);
            setShowArrow(false);
            setShowResults(false);
            return;
        }

        setShowArrow(false);
        setShowResults(false);
        setFieldsSlotWidth(0);
        setArrowSlotSize(0);

        const measureFrame = requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                setArrowSlotSize(ARROW_SLOT_SIZE);
                setFieldsSlotWidth(MAX_FIELD_ROW_WIDTH);
            });
        });

        const arrowTimer = window.setTimeout(() => setShowArrow(true), LAYOUT_EXPAND_MS + 50);
        const resultsTimer = window.setTimeout(() => setShowResults(true), LAYOUT_EXPAND_MS + 500);

        return () => {
            cancelAnimationFrame(measureFrame);
            window.clearTimeout(arrowTimer);
            window.clearTimeout(resultsTimer);
        };
    }, [scannedExtraction]);

    const processFile = async (file: File) => {
        if (file.type !== ACCEPTED_FILE_TYPE) {
            errorSnackbar('Invalid file type. Only PDF files are accepted.');
            return;
        }

        if (file.size > MAX_FILE_SIZE) {
            errorSnackbar('File size is too large. Must be less than 10MB.');
            return;
        }

        setSelectedFile(file);
        onScannedExtractionChange(null);
        onApsScanIdChange(null);
        setIsScanning(true);

        try {
            const result = await scanApsPdf(file);
            const scanId = typeof result?.scanId === 'string' ? result.scanId : null;
            const extraction = await resolveScannedAddress(
                normalizeApsOcrExtraction(result as Record<string, unknown>),
            );

            onScannedExtractionChange(extraction);
            onApsScanIdChange(scanId);
        } catch (error) {
            console.error('APS OCR failed:', error);
            errorSnackbar('Failed to scan APS PDF. Please try again.');
            setSelectedFile(null);
        } finally {
            setIsScanning(false);
        }
    };

    const handleFileSelect = (files: FileList | null) => {
        if (!files || files.length === 0 || isScanning) {
            return;
        }

        if (files.length > 1) {
            errorSnackbar('You can only upload one file at a time');
            return;
        }

        void processFile(files[0]);
    };

    const handleReset = () => {
        setSelectedFile(null);
        onScannedExtractionChange(null);
        onApsScanIdChange(null);
        setFieldsSlotWidth(0);
        setArrowSlotSize(0);
        setShowArrow(false);
        setShowResults(false);
    };

    const displayRows = scannedExtraction ? getDisplayRows(scannedExtraction) : [];
    const extraFieldGroups = scannedExtraction
        ? getExtraExtractedFieldGroups(scannedExtraction)
        : [];
    const isStepComplete = !!scannedExtraction;

    return (
        <NewProspectStep
            stepNumber="✦"
            title="Agreement of Purchase and Sale AI Scan"
            description="Upload an APS document to scan with AI and autofill the details of your matter"
            isComplete={isStepComplete}
            showStatus={isStepComplete}
            isNew={!isStepComplete}
            highlighted={highlighted}
        >
            <Stack spacing={3}>
                {selectedFile ? (
                    <Stack spacing={2} alignItems="center">
                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'center',
                                width: '100%',
                                py: 1,
                            }}
                        >
                            <Stack
                                direction={{ xs: 'column', sm: 'row' }}
                                alignItems="center"
                                sx={{
                                    gap: scannedExtraction ? 2 : 0,
                                    transition: `gap ${LAYOUT_EXPAND_MS}ms ease`,
                                }}
                            >
                                <Box sx={{ position: 'relative', flexShrink: 0 }}>
                                    <PdfFirstPagePreview file={selectedFile} />
                                    {isScanning && <ScanOverlay fileName={selectedFile.name} />}
                                </Box>

                                {scannedExtraction && (
                                    <>
                                        <Box
                                            sx={{
                                                width: { xs: 0, sm: arrowSlotSize },
                                                height: { xs: arrowSlotSize, sm: 'auto' },
                                                overflow: 'hidden',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                flexShrink: 0,
                                                opacity: showArrow ? 1 : 0,
                                                transition: [
                                                    `width ${LAYOUT_EXPAND_MS}ms ease`,
                                                    `height ${LAYOUT_EXPAND_MS}ms ease`,
                                                    'opacity 500ms ease',
                                                ].join(', '),
                                                color: theme.palette.primary.main,
                                            }}
                                        >
                                            <ArrowForwardIcon
                                                sx={{
                                                    fontSize: ARROW_SLOT_SIZE,
                                                    transform: {
                                                        xs: 'rotate(90deg)',
                                                        sm: 'none',
                                                    },
                                                }}
                                            />
                                        </Box>

                                        <Box
                                            sx={{
                                                width: fieldsSlotWidth,
                                                overflow: 'hidden',
                                                flexShrink: 0,
                                                transition: `width ${LAYOUT_EXPAND_MS}ms ease`,
                                            }}
                                        >
                                            <Box
                                                sx={{
                                                    width: MAX_FIELD_ROW_WIDTH,
                                                    boxSizing: 'border-box',
                                                }}
                                            >
                                                <OcrResultRows
                                                    rows={displayRows}
                                                    visible={showResults}
                                                />
                                            </Box>
                                        </Box>
                                    </>
                                )}
                            </Stack>
                        </Box>

                        {!isScanning && (
                            <Typography
                                component="button"
                                type="button"
                                variant="body2"
                                onClick={handleReset}
                                sx={{
                                    border: 'none',
                                    background: 'none',
                                    padding: 0,
                                    color: theme.palette.primary.main,
                                    cursor: 'pointer',
                                    textDecoration: 'underline',
                                    font: 'inherit',
                                }}
                            >
                                Upload a different PDF
                            </Typography>
                        )}
                    </Stack>
                ) : (
                    <PdfUploadDropzone onFileSelect={handleFileSelect} />
                )}

                {scannedExtraction && extraFieldGroups.length > 0 && (
                    <ExtraExtractedProperties
                        key={
                            selectedFile
                                ? `${selectedFile.name}-${selectedFile.lastModified}`
                                : 'scan'
                        }
                        groups={extraFieldGroups}
                        visible={showResults}
                        delayMs={displayRows.length * 120 + 200}
                        values={fieldValues}
                        onFieldChange={onFieldChange}
                    />
                )}
            </Stack>
        </NewProspectStep>
    );
});
